import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { darken } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LanguageIcon from '@mui/icons-material/Language';
import ProfileIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import HelpIcon from '@mui/icons-material/HelpCenterOutlined';
import ChatIcon from '@mui/icons-material/ForumOutlined';
import CommunityIcon from '@mui/icons-material/GroupsOutlined';
import PersonIcon from '@mui/icons-material/Face2';
import StrangerIcon from '@mui/icons-material/Person';
import { getCurrentUser, isLoggedIn, onSessionChange, logout, getLoginURL, getRegisterURL, rememberReturnTo } from '../../common/auth';
import { SURFACE_COLORS, PRIMARY_COLORS } from '../../common/colors';
import { appLink } from '../../common/appLinks';
import { CHAT_URL, DOCS_URL } from '../../common/constants';
import { LOCALES, setLanguage } from '../../i18n/config';

// Account drawer copied from oclmap (components/users/UserMenu.jsx) so the
// menu matches the in-app one for both signed-in and signed-out visitors
// (OpenConceptLab/ocl_issues#2621): same avatar button, menu items, and
// language switcher — only the auth/i18n plumbing is the community site's.

// 45px to match the avatar in oclweb3/oclmap, where both the header button
// and the drawer render UserIcon with the `user-img-small` class (45x45 in
// components/app/App.scss). The header used to hardcode 34, which made the
// signed-in avatar visibly smaller here than in TermBrowser v3
// (OpenConceptLab/ocl_online#159).
const AVATAR_SIZE = 45;

const itemSx = { p: 1, borderRadius: '100px' };
const itemIconSx = { minWidth: 'auto', paddingRight: '14px' };
const pillSx = { height: '40px', borderRadius: '100px', padding: '0 8px', maxWidth: '100%' };
// Chip has no `color` prop here, so MUI treats it as MuiChip-colorDefault —
// that variant's built-in :hover/.Mui-focusVisible rule paints a near-black
// background (theme.palette.text.primary) regardless of the sx backgroundColor
// override above, since the override only styles the resting state. Explicit
// hover colors here win (sx :hover rules are inserted after the component's
// own defaults) instead of falling through to that default.
const hoverSx = color => ({ '&:hover, &.Mui-focusVisible': { backgroundColor: darken(color, 0.08) } });

const UserIcon = ({ authenticated, user, size }) => {
  if (user?.logo_url)
    return <img src={user.logo_url} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }} />;
  return authenticated ? <PersonIcon /> : <StrangerIcon />;
};

const UserMenu = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [languageOpen, setLanguageOpen] = React.useState(false);
  // Deliberately NOT read synchronously (isLoggedIn()/getCurrentUser()
  // straight in the render body, as this used to): localStorage is
  // browser-only real state a returning visitor's browser carries but the
  // server (prerendering's happy-dom environment) never does. Reading it
  // on the very first render — the SSR pass and the client's initial
  // hydration pass alike — makes them disagree on whole subtrees (signed
  // in vs signed out), a structural mismatch severe enough that React
  // abandons the prerendered HTML entirely (errors 418/423) instead of
  // hydrating it. Defaulting to signed-out here matches what SSR always
  // renders; the effect corrects it right after mount, which is a normal
  // post-hydration state update, not a hydration diff.
  const [authState, setAuthState] = React.useState({ authenticated: false, user: {} });
  React.useEffect(() => {
    const update = () => setAuthState({ authenticated: isLoggedIn(), user: getCurrentUser() || {} });
    update();
    return onSessionChange(update);
  }, []);
  const { authenticated, user } = authState;
  const current = (i18n.resolvedLanguage || 'en').split('-')[0];
  const selectedLocale = LOCALES.find(locale => locale.code === current) || LOCALES[0];
  const profileURL = user.url ? appLink('termBrowser', user.url) : undefined;

  const onClose = () => setOpen(false);
  const onLanguageSelect = code => {
    setLanguage(code);
    setLanguageOpen(false);
  };

  return (
    <>
      <IconButton color="primary" aria-label={t('auth.account_menu_aria')} onClick={() => setOpen(true)}>
        <UserIcon authenticated={authenticated} user={user} size={AVATAR_SIZE} />
      </IconButton>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{ zIndex: 1202 }}
        slotProps={{ paper: { sx: { minWidth: '360px', borderRadius: '16px', padding: '12px' } } }}>
        <Box sx={{ p: '15px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', p: '8px 0' }}>
            {
              authenticated &&
                <>
                  <IconButton color="primary" sx={{ p: user.logo_url ? 0 : 1 }}>
                    <UserIcon authenticated={authenticated} user={user} size={AVATAR_SIZE} />
                  </IconButton>
                  <Box sx={{ ml: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 'bold' }}>{user.username}</Typography>
                    <Typography>{user.name}</Typography>
                  </Box>
                </>
            }
            <IconButton onClick={onClose} aria-label={t('nav.close_menu')} sx={{ ml: 'auto' }}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </Box>
          {
            authenticated ?
              <List>
                <ListItemButton sx={itemSx} component="a" href={profileURL} disabled={!profileURL}>
                  <ListItemIcon sx={itemIconSx}>
                    <ProfileIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('menu.my_profile')} />
                </ListItemButton>
              </List> :
              <Box sx={{ mb: '24px' }}>
                <Chip clickable component="a" label={t('nav.sign_in')} href='#' onClick={e => { e.preventDefault(); rememberReturnTo(); getLoginURL().then(url => { window.location.href = url; }); }} sx={{ ...pillSx, backgroundColor: 'surface.s90', minWidth: '92px', ...hoverSx(SURFACE_COLORS.s90) }} />
                <Button size="small" component="a" href='#' onClick={e => { e.preventDefault(); getRegisterURL().then(url => { window.location.href = url; }); }} sx={{ fontSize: '14px', marginLeft: '16px', textTransform: 'none' }}>
                  {t('auth.register')}
                </Button>
              </Box>
          }
          <Divider />
          <List>
            <ListItemButton sx={itemSx} onClick={() => setLanguageOpen(!languageOpen)}>
              <ListItemIcon sx={itemIconSx}>
                <LanguageIcon />
              </ListItemIcon>
              <ListItemText primary={selectedLocale.label} secondary={t('nav.language')} />
              {languageOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={languageOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {
                  LOCALES.map(locale => (
                    <ListItemButton sx={{ pl: 5.75, borderRadius: '100px' }} selected={locale.code === selectedLocale.code} key={locale.code} onClick={() => onLanguageSelect(locale.code)}>
                      <ListItemText primary={locale.code.toUpperCase()} secondary={locale.label} />
                    </ListItemButton>
                  ))
                }
              </List>
            </Collapse>
            <ListItemButton sx={itemSx} component="a" href={profileURL ? appLink('termBrowser', `${user.url}settings`) : undefined} disabled={!profileURL}>
              <ListItemIcon sx={itemIconSx}>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary={t('menu.settings')} />
            </ListItemButton>
          </List>
          <Divider />
          <List>
            <ListItemButton sx={itemSx} component={RouterLink} to="/" onClick={onClose} selected>
              <ListItemIcon sx={itemIconSx}>
                <CommunityIcon />
              </ListItemIcon>
              <ListItemText primary={t('menu.community_website')} />
            </ListItemButton>
            <ListItemButton sx={itemSx} component="a" href={DOCS_URL} target="_blank" rel="noopener noreferrer">
              <ListItemIcon sx={itemIconSx}>
                <HelpIcon />
              </ListItemIcon>
              <ListItemText primary={t('menu.help_and_documentation')} />
            </ListItemButton>
            <ListItemButton sx={itemSx} component="a" href={CHAT_URL} target="_blank" rel="noopener noreferrer">
              <ListItemIcon sx={itemIconSx}>
                <ChatIcon />
              </ListItemIcon>
              <ListItemText primary={t('menu.chat')} />
            </ListItemButton>
          </List>
          <Divider />
          {
            authenticated &&
              <Box sx={{ mt: '24px' }}>
                {/* A light tint is primary.80; primary.dark is the hover fill, a dark shade (colors.jsx). */}
                <Chip clickable label={t('auth.sign_out')} onClick={logout} sx={{ ...pillSx, backgroundColor: 'primary.80', ...hoverSx(PRIMARY_COLORS['80']) }} />
              </Box>
          }
        </Box>
      </Drawer>
    </>
  );
};

export default UserMenu;
